import React, { useState } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import Navbar from '../components/Navbar';
import LanguageToggle from '../components/LanguageToggle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QuestionGeneratorPage = () => {
  const { t } = useLanguage();
  const [discipline, setDiscipline] = useState('');
  const [topic, setTopic] = useState('');
  const [difficulty, setDifficulty] = useState('medium');
  const [numberOfQuestions, setNumberOfQuestions] = useState(1);
  const [questions, setQuestions] = useState('');
  const [loading, setLoading] = useState(false);

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!discipline || !topic.trim()) return;

    // Check usage limit
    try {
      const limitCheck = await axios.post(
        `${API}/subscriptions/check-limit`,
        { action_type: 'ai_question' },
        { withCredentials: true }
      );

      if (!limitCheck.data.allowed) {
        const reason = limitCheck.data.reason;
        if (reason === 'daily_limit_reached') {
          alert(language === 'tr' 
            ? `⚠️ Günlük AI soru limitinize ulaştınız (${limitCheck.data.limit}). Yarın tekrar deneyin veya planınızı yükseltin.`
            : `⚠️ Daily AI question limit reached (${limitCheck.data.limit}). Try again tomorrow or upgrade your plan.`);
        } else if (reason === 'monthly_limit_reached') {
          alert(language === 'tr'
            ? `⚠️ Aylık AI soru limitinize ulaştınız (${limitCheck.data.limit}). Planınızı yükseltin.`
            : `⚠️ Monthly AI question limit reached (${limitCheck.data.limit}). Please upgrade your plan.`);
        }
        return;
      }
    } catch (error) {
      console.error('Error checking limit:', error);
      // Continue anyway if limit check fails
    }

    setLoading(true);
    setQuestions('');

    try {
      const response = await axios.post(`${API}/generate-questions`, {
        discipline,
        topic,
        level: difficulty,
        n_items: numberOfQuestions,
        formats: "TYT multiple-choice + AP FRQ"
      });

      setQuestions(response.data.questions);

      // Track usage
      try {
        await axios.post(
          `${API}/subscriptions/track-usage`,
          { action_type: 'ai_question' },
          { withCredentials: true }
        );
      } catch (error) {
        console.error('Error tracking usage:', error);
      }
    } catch (error) {
      console.error('Error generating questions:', error);
      setQuestions(t('error') + ': ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setDiscipline('');
    setTopic('');
    setDifficulty('medium');
    setNumberOfQuestions(1);
    setQuestions('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <LanguageToggle />
      <Navbar title={t('question_generator')} />

      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          
          {/* Input Panel */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
              <span className="text-3xl mr-3">⚙️</span>
              {t('generate_questions')}
            </h2>
            
            <form onSubmit={handleGenerate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('discipline')}
                </label>
                <select
                  value={discipline}
                  onChange={(e) => setDiscipline(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                  data-testid="question-discipline-select"
                >
                  <option value="">{t('select_discipline')}</option>
                  <option value="Physics">{t('physics')}</option>
                  <option value="Mathematics">{t('mathematics')}</option>
                  <option value="Chemistry">{t('chemistry')}</option>
                  <option value="Biology">{t('biology')}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('topic')}
                </label>
                <input
                  type="text"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  placeholder={t('enter_topic')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                  data-testid="topic-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('difficulty')}
                </label>
                <select
                  value={difficulty}
                  onChange={(e) => setDifficulty(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  data-testid="difficulty-select"
                >
                  <option value="easy">{t('easy')}</option>
                  <option value="medium">{t('medium')}</option>
                  <option value="hard">{t('hard')}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('number_of_questions')}
                </label>
                <input
                  type="number"
                  value={numberOfQuestions}
                  onChange={(e) => setNumberOfQuestions(Math.max(1, Math.min(5, parseInt(e.target.value) || 1)))}
                  min="1"
                  max="5"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  data-testid="number-input"
                />
              </div>

              <div className="flex space-x-4">
                <button
                  type="submit"
                  disabled={loading || !discipline || !topic.trim()}
                  className="flex-1 bg-gradient-to-r from-pink-600 to-rose-600 text-white px-6 py-3 rounded-xl hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 font-semibold"
                  data-testid="generate-button"
                >
                  {loading ? t('loading') : t('generate')}
                </button>
                <button
                  type="button"
                  onClick={handleReset}
                  className="bg-gray-200 text-gray-800 px-6 py-3 rounded-xl hover:bg-gray-300 transition-colors font-semibold"
                  data-testid="reset-button"
                >
                  {t('new_chat')}
                </button>
              </div>
            </form>
          </div>

          {/* Results Panel */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
              <span className="text-3xl mr-3">📝</span>
              {t('generated_questions')}
            </h2>
            
            {questions ? (
              <div className="prose max-w-none" data-testid="generated-questions">
                <div className="bg-gradient-to-r from-pink-50 to-rose-50 p-6 rounded-xl">
                  <div className="whitespace-pre-wrap text-gray-800">{questions}</div>
                </div>
              </div>
            ) : (
              <div className="text-center text-gray-400 mt-12">
                <svg className="w-24 h-24 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-lg">{t('generate_questions')}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuestionGeneratorPage;